import { SCOPES } from '../scopes.js';
import { TEST_CREDENTIAL_CATEGORY } from './test-credential-categories.js';
import { TEST_CREDENTIAL_TYPES } from './test-credential-types.js';
/**
 * Master list of public test credentials, used for testing various Transact login user experiences.
 */
export const TEST_CREDENTIALS = {
    TEST_GOOD: {
        title: 'Successful Operation',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-good',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-good@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550100',
        description: 'Test a successful operation.',
        category: TEST_CREDENTIAL_CATEGORY.SUCCESS,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.PAY_LINK, SCOPES.USER_LINK]
    },
    TEST_CODE_MFA: {
        title: 'Code-based MFA',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-code-mfa',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-code-mfa@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550101',
        description: 'Test an authentication that includes a device code based MFA flow.',
        category: TEST_CREDENTIAL_CATEGORY.SUCCESS,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_PUSH_MFA: {
        title: 'Push-based MFA',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-push-mfa',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-push-mfa@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550102',
        description: 'Test an authentication that simulates push-based MFA.',
        category: TEST_CREDENTIAL_CATEGORY.SUCCESS,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_QUESTION_MFA: {
        title: 'Question-based MFA',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-question-mfa',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-question-mfa@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550103',
        description: 'Test an authentication that simulates question-based MFA.',
        category: TEST_CREDENTIAL_CATEGORY.SUCCESS,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_SYSTEM_UNAVAILABLE: {
        title: 'System Unavailable',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-system-unavailable',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-system-unavailable@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550104',
        description: 'Test the user experience during a third-party system outage.',
        category: TEST_CREDENTIAL_CATEGORY.CONNECTOR_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_UNKNOWN_FAILURE: {
        title: 'Unknown Failure',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-unknown-failure',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-unkown-failure@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550105',
        description: 'Test the user experience when there is an unexpected error.',
        category: TEST_CREDENTIAL_CATEGORY.CONNECTOR_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_SESSION_TIMEOUT: {
        title: 'Session Timeout',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-session-timeout',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-session-timeout@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550106',
        description: 'Test the user experience when the auth session has timed out.',
        category: TEST_CREDENTIAL_CATEGORY.CONNECTOR_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_CONNECTION_ERROR: {
        title: 'Connection Error',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-connection-error',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-connection-error@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550107',
        description: 'Test the user experience when there is a connection error caused by a network failure.',
        category: TEST_CREDENTIAL_CATEGORY.CONNECTOR_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_HIGH_LATENCY: {
        title: 'High Latency',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-high-latency',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-high-latency@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550108',
        description: 'Test the flow which occurs when there is high latency communicating with backend systems.',
        category: TEST_CREDENTIAL_CATEGORY.CONNECTOR_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_POST_AUTH_DELAY: {
        title: 'Post-auth Delay',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-post-auth-delay',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-post-auth-delay@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550109',
        description: 'Test the flow when there is a post-auth delay happening. This may occur due to an unanticipated change in the third-party system.',
        category: TEST_CREDENTIAL_CATEGORY.CONNECTOR_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_FAILURE: {
        title: 'Post-auth Failure',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-failure',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-failure@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550110',
        description: 'Test a failure that occurs after a successful authentication.',
        category: TEST_CREDENTIAL_CATEGORY.CONNECTOR_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_DISTRIBUTION_NOT_SUPPORTED: {
        title: 'Unsupported Deposit',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-distribution-not-supported',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-distribution-not-supported@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550111',
        description: 'Test a user who enters an unsupported deposit amount.',
        category: TEST_CREDENTIAL_CATEGORY.PAYROLL_SYSTEM_CONFIGURATION_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_ROUTING_NUMBER_NOT_SUPPORTED: {
        title: 'Unsupported Routing Number',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-routing-number-not-supported',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-routing-number-not-supported@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550112',
        description: 'Test a user whose payroll system rejects the routing number of the target deposit account.',
        category: TEST_CREDENTIAL_CATEGORY.PAYROLL_SYSTEM_CONFIGURATION_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_PRODUCT_NOT_SUPPORTED: {
        title: 'Unsupported Product',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-product-not-supported',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-product-not-supported@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550113',
        description: 'Test a user whose payroll system does not allow the operation.',
        category: TEST_CREDENTIAL_CATEGORY.PAYROLL_SYSTEM_CONFIGURATION_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_BAD: {
        title: 'Unsuccessful Authentication',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-bad',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-bad@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550114',
        description: 'Test an unsuccessful authentication.',
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_LOCKOUT: {
        title: 'Account Lockout',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-lockout',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-lockout@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550115',
        description: 'Test a user who has been locked out of their account.',
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_ACCOUNT_UNUSABLE: {
        title: 'Unsupported Account',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-account-unusable',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-account-unusable@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550116',
        description: 'Test a user whose payroll account rejects the target deposit account.',
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_ENROLLED_IN_PAYCARD: {
        title: 'Paycard Enrolled',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-enrolled-in-paycard',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-enrolled-in-paycard@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550117',
        description: 'Test a user enrolled in a paycard, which prevents payment via direct deposit.',
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.USER_LINK]
    },
    TEST_EXPIRED: {
        title: 'Expired Password',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-expired',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-expired@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550118',
        description: 'Test a user whose payroll password has expired.',
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_TRANSACTION_PENDING: {
        title: 'Pending Transaction',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-transaction-pending',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-transaction-pending@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550119',
        description: 'Test a user who already has a direct deposit change in progress.',
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.USER_LINK]
    },
    TEST_ACCOUNT_SETUP_INCOMPLETE: {
        title: 'Incomplete Account',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-account-setup-incomplete',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-account-setup-incomplete@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550120',
        description: 'Test a user who has not fully onboarded to their employee payroll system.',
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_WORK_STATUS_TERMINATED: {
        title: 'Inactive Employee',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-work-status-terminated',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-work-status-terminated@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550121',
        description: 'Test a user who is not an active employee in the payroll system.',
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.EMPLOYER_LINK, SCOPES.USER_LINK]
    },
    TEST_SUBSCRIPTION_INACTIVE: {
        title: 'Subscription Inactive',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-subscription-inactive',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-subscription-inactive@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550122',
        description: "Test a user who doesn't have an active subscription with the service provider.",
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.PAY_LINK]
    },
    TEST_BUNDLE_WRONG_PROVIDER: {
        title: 'Subscription Managed by Partner Provider',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-bundle-wrong-provider',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-bundle-wrong-provider@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550123',
        description: 'Test a user whose subscription bundle is managed by another service.',
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.PAY_LINK]
    },
    TEST_PAYMENT_METHOD_LOCKED: {
        title: 'Payment Method Locked',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-payment-method-locked',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-payment-method-locked@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550124',
        description: "Test a user whose service provider has temporarily disallowed updates to the user's payment method.",
        category: TEST_CREDENTIAL_CATEGORY.USER_ISSUE,
        scopes: [SCOPES.PAY_LINK]
    },
    TEST_PAYMENT_METHOD_NOT_SUPPORTED: {
        title: 'Payment Method Not Supported',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-payment-method-not-supported',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-payment-method-not-supported@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550125',
        description: "Test a user whose chosen payment method isn't supported by the service provider.",
        category: TEST_CREDENTIAL_CATEGORY.SERVICE_PROVIDER_ISSUE,
        scopes: [SCOPES.PAY_LINK]
    },
    TEST_PAYMENT_SWITCH_UNSUCCESSFUL: {
        title: 'Payment Switch Unsuccessful',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-payment-switch-unsuccessful',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-payment-switch-unsuccessful@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550126',
        description: 'Test a user whose payment method could not be updated. We use this generic fail reason when none of the other fail reasons apply.',
        category: TEST_CREDENTIAL_CATEGORY.SERVICE_PROVIDER_ISSUE,
        scopes: [SCOPES.PAY_LINK]
    },
    TEST_VALIDATION_FAILURE: {
        title: 'Data Validation Failure',
        [TEST_CREDENTIAL_TYPES.USERNAME]: 'test-validation-failure',
        [TEST_CREDENTIAL_TYPES.EMAIL]: 'test-validation-failure@example.com',
        [TEST_CREDENTIAL_TYPES.PHONE_NUMBER]: '5555550127',
        description: 'Test a user whose data does not pass data validation.',
        category: TEST_CREDENTIAL_CATEGORY.SERVICE_PROVIDER_ISSUE,
        scopes: [SCOPES.USER_LINK]
    }
};
//# sourceMappingURL=test-credentials.js.map